import { SourceCard } from "@/components/sources/source-card";
import type { SourceConfig } from "@/lib/api";
import { dotClass, sourceColor } from "@/lib/source-color";
import { cn } from "@/lib/utils";

interface Props {
  vendor: string;
  sources: SourceConfig[];
  onRun: (sourceId: string) => void;
  runningId: string | null;
}

/** One vendor = one cluster. Multiple sources under the same vendor
 *  (e.g. Jack Kellogg's alerts feed + portfolio + tools) render as
 *  sibling cards inside a shared heading. */
export function VendorGroup({ vendor, sources, onRun, runningId }: Props) {
  const label = humanizeVendor(vendor);
  // Colour the group heading by the first source's palette — gives a
  // visual anchor matching the feed rows for this vendor. Grouping is
  // only rendered when there's at least one source, so sources[0] is
  // always defined.
  const color = sourceColor(sources[0].id);
  return (
    <section>
      <header className="mb-3 flex items-baseline gap-3">
        <span className={cn("size-2 shrink-0 rounded-full ring-2 ring-black/40", dotClass(color))} />
        <h2 className="text-sm font-semibold tracking-tight text-fg">{label}</h2>
        <span className="text-xs text-muted-fg">
          {sources.length} {sources.length === 1 ? "page" : "pages"}
        </span>
      </header>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {sources.map((s) => (
          <SourceCard key={s.id} source={s} onRun={onRun} running={runningId === s.id} />
        ))}
      </div>
    </section>
  );
}

/** "jack_kellogg" → "Jack Kellogg"; fallback to the raw slug on miss. */
function humanizeVendor(v: string): string {
  const named: Record<string, string> = {
    jack_kellogg: "Jack Kellogg",
    jeff_zananiri: "Jeff Zananiri",
    smartdata: "SmartData",
    stockstotrade: "StocksToTrade",
    timothy_sykes: "Timothy Sykes",
    tradingview: "TradingView",
  };
  return named[v] ?? v.replace(/_/g, " ");
}
